/**
 * The side flyout shared by the drawing-tool surfaces: the `DrawingBar`'s
 * per-group picker on the chart's left edge, and the `DrawingToolsMenu`'s
 * per-category submenu in the toolbar dropdown.
 *
 * Lifted out of `DrawingBar.tsx` so the two cannot drift, and so the menu's
 * submenus behave exactly like the bar's pickers.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Z_CHROME } from './zLayers'
import { HotkeyBadge } from './HotkeyBadge'

/** Gap between the anchor row and the flyout, and the viewport safe margin. */
const FLYOUT_GAP = 3
const VIEWPORT_MARGIN = 8

export function Flyout({ children }: { children: React.ReactNode }) {
  // Fixed positioning (measured off the anchor) instead of absolute: the bar's
  // tool list is an overflow-y:auto scroller and the toolbar dropdown is a
  // popover, either of which would clip an absolutely-positioned flyout poking
  // out of its edge.
  //
  // NB: `fixed` is NOT necessarily viewport-relative here. The bar's
  // backdrop-filter, and the popover's transform, each make an ancestor the
  // containing block for fixed descendants. So the flyout first renders
  // (hidden) at {0,0}, measures where that actually landed, and offsets by the
  // delta. Correct for any containing block, which is what lets the menu's
  // submenus escape the popover that hosts them.
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  // Drives the top-to-bottom reveal. Deliberately separate from `pos`: the
  // flyout first renders (hidden) at {0,0} to measure itself, and starting the
  // reveal there would play the whole animation at the wrong coordinates. We
  // unclip on the frame AFTER the measured position lands.
  const [revealed, setRevealed] = useState(false)
  useLayoutEffect(() => {
    const el = ref.current
    const anchor = el?.parentElement
    if (!el || !anchor) return
    const anchorRect = anchor.getBoundingClientRect()
    const baseRect = el.getBoundingClientRect() // where {0,0} renders
    // Clamp so tall pickers stay on screen (viewport coords).
    const desiredTop = Math.max(
      VIEWPORT_MARGIN,
      Math.min(anchorRect.top, window.innerHeight - el.offsetHeight - VIEWPORT_MARGIN),
    )
    // Open to the RIGHT of the anchor by default. A panel docked near the
    // window's right edge leaves no room there, so flip to the anchor's LEFT
    // rather than letting the flyout run off screen (where its tools would be
    // unreachable). Falls back to the right if neither side fits.
    const width = el.offsetWidth
    const rightLeft = anchorRect.right + FLYOUT_GAP
    const fitsRight = rightLeft + width <= window.innerWidth - VIEWPORT_MARGIN
    const flippedLeft = anchorRect.left - FLYOUT_GAP - width
    const desiredLeft = fitsRight
      ? rightLeft
      : flippedLeft >= VIEWPORT_MARGIN
        ? flippedLeft
        : Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - width)
    setPos({ top: desiredTop - baseRect.top, left: desiredLeft - baseRect.left })
  }, [])

  useEffect(() => {
    if (!pos) return
    const id = requestAnimationFrame(() => setRevealed(true))
    return () => cancelAnimationFrame(id)
  }, [pos])

  // Honour the OS "reduce motion" setting - snap straight to open.
  const reduceMotion =
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  const open = revealed || reduceMotion

  return (
    <div
      ref={ref}
      data-tool-flyout=""
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        ...(pos ?? { top: 0, left: 0, visibility: 'hidden' }),
        // `inset(top right bottom left)` - a 100% bottom inset collapses the
        // box to its top edge, so easing it to 0 wipes the content downward.
        // Clip (not scale) so the items never squash or shift while opening.
        clipPath: open ? 'inset(0 0 0 0)' : 'inset(0 0 100% 0)',
        transitionProperty: 'clip-path',
        transitionDuration: reduceMotion ? '0ms' : '250ms',
        transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        zIndex: Z_CHROME,
      }}
      className="fixed max-h-80 min-w-[190px] overflow-y-auto rounded-md border border-primary/15 bg-popover p-1 shadow-lg shadow-black/30"
    >
      {children}
    </div>
  )
}

export function FlyoutItem({
  children,
  icon,
  active,
  hotkey,
  onClick,
  trailing,
}: {
  children: React.ReactNode
  icon?: React.ReactNode
  active?: boolean
  /** Keyboard shortcut label, right-aligned after the tool name. */
  hotkey?: string
  onClick?: () => void
  /**
   * A control after the hotkey (the favorites star). Rendered as a SIBLING of
   * the row button, not inside it: nested buttons are invalid HTML and the
   * inner click would bubble into the row's own onClick.
   */
  trailing?: React.ReactNode
}) {
  return (
    <div className="group/flyout-item relative flex items-center">
      <button
        onClick={onClick}
        className={
          'flex w-full items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1 text-left text-xs chrome-press ' +
          (trailing ? 'pr-6 ' : '') +
          (active
            ? 'bg-accent text-primary'
            : 'text-[hsl(var(--chart-text)/0.72)] chrome-press hover:bg-accent hover:text-[hsl(var(--chart-text)/0.9)]')
        }
      >
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
          {icon}
        </span>
        <span className="grow">{children}</span>
        {hotkey && <HotkeyBadge hotkey={hotkey} />}
      </button>
      {trailing && <span className="absolute right-1 flex items-center">{trailing}</span>}
    </div>
  )
}
